/**
 * QrStudioPage — /crm/qr-studio (deliberately NOT in any sidebar/nav; reached
 * by direct URL only while the feature is being tested).
 *
 * The home for trackable QR codes + lead-capture landing pages:
 *   QR Codes  — dynamic codes encoding fhl.one/q/{code}; destination editable
 *               after printing (form, booking page, or custom URL)
 *   Forms     — mobile-first public pages at fhl.one/f/{slug} that create CRM
 *               contacts through the canonical ingest pipeline
 *   Analytics — scans (even without submission), conversion, devices,
 *               submissions browser
 *
 * Loan officers manage their own; CRM Admins / SuperAdmins get a "managing
 * for" picker to build on any LO's behalf (rows are owned by the TARGET user,
 * enforced server-side by the manage callables).
 *
 * Reads: anon Supabase (crm_lead_forms / crm_qr_codes are SELECT-open, like
 * booking pages). Writes: manageLeadForms / manageQrCodes callables.
 * Scan/submission data: getQrFormAnalytics callable (tables are locked down).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Badge,
  Box,
  Center,
  Heading,
  HStack,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmUsers } from '../../hooks/useCrmUsers';
import { supabase } from '../../utils/supabase';
import { call } from '../../components/CRM/QrStudio/qrStudioUtils';
import QrCodesTab from '../../components/CRM/QrStudio/QrCodesTab';
import FormsTab from '../../components/CRM/QrStudio/FormsTab';
import AnalyticsTab from '../../components/CRM/QrStudio/AnalyticsTab';

export default function QrStudioPage() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { users: crmUsers } = useCrmUsers();
  const pageBg = useColorModeValue('gray.50', 'gray.900');

  const isAdmin =
    SuperAdmin === true ||
    currentUser?.CRMRole === 'CRM Admin' ||
    (Array.isArray(Roles) && Roles.includes('CRM Admin'));

  const [targetUserId, setTargetUserId] = useState(null);
  const [forms, setForms] = useState([]);
  const [qrs, setQrs] = useState([]);
  const [bookingPages, setBookingPages] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Default to managing yourself
  useEffect(() => {
    if (!targetUserId && currentUser?.uid) setTargetUserId(currentUser.uid);
  }, [currentUser, targetUserId]);

  const targetUser = useMemo(() => {
    if (targetUserId === currentUser?.uid) {
      return { uid: currentUser?.uid, name: currentUser?.Name || currentUser?.displayName || '' };
    }
    const match = crmUsers.find((u) => u.uid === targetUserId);
    return match ? { uid: match.uid, name: match.name } : { uid: targetUserId, name: '' };
  }, [targetUserId, currentUser, crmUsers]);

  const loadData = useCallback(async () => {
    if (!targetUserId) return;
    setLoading(true);
    try {
      const [formsRes, qrsRes, pagesRes] = await Promise.all([
        supabase.from('crm_lead_forms').select('*').eq('owner_id', targetUserId).order('created_at', { ascending: false }),
        supabase.from('crm_qr_codes').select('*').eq('owner_id', targetUserId).order('created_at', { ascending: false }),
        supabase.from('crm_booking_pages').select('id, title, owner_handle, slug').eq('owner_id', targetUserId).eq('is_active', true).order('is_default', { ascending: false }),
      ]);
      setForms(formsRes.data || []);
      setQrs(qrsRes.data || []);
      setBookingPages(pagesRes.data || []);
    } catch (err) {
      console.error('[QrStudio] data load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  const loadOverview = useCallback(async () => {
    if (!targetUserId) return;
    setOverviewLoading(true);
    try {
      const result = await call('getQrFormAnalytics', { userId: targetUserId, view: 'overview' });
      setOverview(result);
    } catch (err) {
      console.error('[QrStudio] overview load failed:', err);
    } finally {
      setOverviewLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    setOverview(null);
    loadData();
    loadOverview();
  }, [loadData, loadOverview]);

  const refreshAll = useCallback(() => {
    loadData();
    loadOverview();
  }, [loadData, loadOverview]);

  const hasCrmAccess = currentUser?.CRMUser === true || isAdmin;

  if (!hasCrmAccess) {
    return (
      <Center minH="60vh" flexDir="column" px={4}>
        <Heading size="md" color="gray.600" mb={2}>QR Studio</Heading>
        <Text color="gray.500">You need Origin CRM access to use this page.</Text>
      </Center>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh" px={{ base: 3, md: 8 }} py={6}>
      <HStack justify="space-between" align="start" mb={6} flexWrap="wrap" spacing={4}>
        <VStack align="start" spacing={1}>
          <HStack>
            <Heading size="lg">QR Studio</Heading>
            <Badge colorScheme="purple" mt={1}>Beta</Badge>
          </HStack>
          <Text color="gray.500" fontSize="sm">
            Trackable QR codes, lead-capture landing pages, and booking handoffs — scans and submissions land in Origin automatically.
          </Text>
        </VStack>

        {isAdmin && (
          <HStack flexShrink={0}>
            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">Managing for</Text>
            <Select
              size="sm"
              maxW="260px"
              value={targetUserId || ''}
              onChange={(e) => setTargetUserId(e.target.value)}
            >
              {currentUser?.uid && (
                <option value={currentUser.uid}>
                  {(currentUser?.Name || 'Me')} (me)
                </option>
              )}
              {crmUsers
                .filter((u) => u.uid !== currentUser?.uid)
                .map((u) => (
                  <option key={u.uid} value={u.uid}>{u.name || u.email}</option>
                ))}
            </Select>
          </HStack>
        )}
      </HStack>

      {loading && forms.length === 0 && qrs.length === 0 ? (
        <Center py={24}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Center>
      ) : (
        <Tabs colorScheme="blue" isLazy>
          <TabList>
            <Tab fontWeight="semibold">QR Codes {qrs.length > 0 && <Badge ml={2}>{qrs.length}</Badge>}</Tab>
            <Tab fontWeight="semibold">Forms {forms.length > 0 && <Badge ml={2}>{forms.length}</Badge>}</Tab>
            <Tab fontWeight="semibold">Analytics</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <QrCodesTab
                targetUserId={targetUserId}
                qrs={qrs}
                forms={forms}
                bookingPages={bookingPages}
                perQr={overview?.perQr}
                onChanged={refreshAll}
              />
            </TabPanel>
            <TabPanel px={0}>
              <FormsTab
                targetUser={targetUser}
                targetUserId={targetUserId}
                forms={forms}
                bookingPages={bookingPages}
                perForm={overview?.perForm}
                onChanged={refreshAll}
              />
            </TabPanel>
            <TabPanel px={0}>
              <AnalyticsTab
                targetUserId={targetUserId}
                qrs={qrs}
                forms={forms}
                overview={overview}
                overviewLoading={overviewLoading}
                onRefreshOverview={loadOverview}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}
